import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND_NAME } from '../../config/brand';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCreator?: string;
  jsonLd?: any;
  noindex?: boolean;
  children?: React.ReactNode;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = `${BRAND_NAME} - Find Your Perfect Rental Property`,
  description = `${BRAND_NAME} is your trusted platform for finding and managing rental properties. Browse apartments, houses, and commercial spaces with ease.`,
  keywords = `${BRAND_NAME}, rental properties, apartments for rent, houses for rent, property management, real estate, rental listings`,
  canonical,
  ogTitle,
  ogDescription,
  ogImage = 'https://res.cloudinary.com/dozkyuutd/image/upload/w_1200,h_630,c_fill/v1758607967/rentalapp/Screenshot_2025-09-23_at_11.37.45_ompmp7.png',
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage = 'https://res.cloudinary.com/dozkyuutd/image/upload/w_1200,h_630,c_fill/v1758607967/rentalapp/Screenshot_2025-09-23_at_11.37.45_ompmp7.png',
  twitterCreator = '@rentbhai',
  jsonLd,
  noindex = false,
  children
}) => {
  const siteUrl = window.location.origin;
  const currentUrl = window.location.href;

  // Use provided values or fallback to defaults
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalOgUrl = ogUrl || currentUrl;
  const finalOgImage = ogImage.startsWith('http') ? ogImage : `${siteUrl}${ogImage}`;

  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || finalOgImage;

  // Default JSON-LD for organization
  const defaultJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": BRAND_NAME,
    "description": "Your trusted platform for rental properties",
    "url": siteUrl,
    "logo": `${siteUrl}/assets/rentBhaiLogo.svg`,
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@rentbhai.com",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": ["en", "hi"]
    },
    "sameAs": [
      "https://facebook.com/rentbhai",
      "https://twitter.com/rentbhai",
      "https://linkedin.com/company/rentbhai",
      "https://instagram.com/rentbhai"
    ]
  };

  const finalJsonLd = jsonLd || defaultJsonLd;

  // All meta and JSON-LD are rendered via Helmet to avoid duplicate tags/scripts

  return (
    <Helmet defer={false} prioritizeSeoTags>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={BRAND_NAME} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Robots Meta Tag */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:secure_url" content={finalOgImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalOgTitle} />
      <meta property="og:site_name" content={BRAND_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={finalOgUrl} />
      <meta property="twitter:title" content={finalTwitterTitle} />
      <meta property="twitter:description" content={finalTwitterDescription} />
      <meta property="twitter:image" content={finalTwitterImage} />
      <meta property="twitter:creator" content={twitterCreator} />
      <meta property="twitter:site" content="@rentbhai" />

      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#8B5CF6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />

      {/* Language and Region */}
      <meta httpEquiv="content-language" content="en-IN" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />

      {/* JSON-LD Structured Data */}
      {finalJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(finalJsonLd)}
        </script>
      )}

      {/* Additional custom meta tags */}
      {children}
    </Helmet>
  );
};

export default SEOHead;
